import { Close, Content, Description, Overlay, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type ElementRef, HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';
const Sheet = Root;
const SheetTrigger = Trigger;
const SheetClose = Close;
const SheetPortal = Portal;
const SheetOverlay = forwardRef<ElementRef<typeof Overlay>, ComponentPropsWithoutRef<typeof Overlay>>(({
  className,
  ...props
}, ref) => <Overlay className={cn('fixed inset-0 z-50 bg-black/80 backdrop-blur-[1px]  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} {...props} ref={ref} />);
SheetOverlay.displayName = Overlay.displayName;
const sheetVariants = cva('fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500', {
  variants: {
    side: {
      top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
      bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom rounded-t-xl',
      left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
      right: 'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm'
    }
  },
  defaultVariants: {
    side: 'right'
  }
});
interface SheetContentProps extends ComponentPropsWithoutRef<typeof Content>, VariantProps<typeof sheetVariants> {
  isCloseButtonRightJustify?: boolean;
  /**
   * Hide default close button (X icon).
   * Use this when you want to render a custom close control inside content.
   */
  hideCloseButton?: boolean;
  /** 背景オーバーレイを描画しない。非モーダルとなり、背面の操作が可能で外側クリックでは閉じない */
  hideOverlay?: boolean;
}
function preventDismissWhenOverlayHidden(hideOverlay: boolean | undefined): Pick<ComponentPropsWithoutRef<typeof Content>, 'onFocusOutside' | 'onInteractOutside' | 'onPointerDownOutside'> {
  if (!hideOverlay) {
    return {};
  }
  const preventDismiss = (event: Event) => {
    event.preventDefault();
  };
  return {
    onPointerDownOutside: preventDismiss,
    onInteractOutside: preventDismiss,
    onFocusOutside: preventDismiss
  };
}
const SheetContent = forwardRef<ElementRef<typeof Content>, SheetContentProps>(({
  side = 'right',
  className,
  children,
  isCloseButtonRightJustify,
  hideCloseButton,
  hideOverlay,
  ...props
}, ref) => <SheetPortal>
      {!hideOverlay && <SheetOverlay />}
      <Content ref={ref} className={cn(sheetVariants({
    side
  }), className)} {...preventDismissWhenOverlayHidden(hideOverlay)} {...props}>
        {children}
        {!hideCloseButton && <Close className={cn('absolute', isCloseButtonRightJustify ? 'right-16' : 'right-4', 'top-4 rounded-sm opacity-70 ring-offset-background transition-opacity', 'hover:opacity-100', 'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2', 'disabled:pointer-events-none', 'data-[state=open]:bg-secondary')}>
            <Icon iconName="XIcon" className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </Close>}
      </Content>
    </SheetPortal>);
SheetContent.displayName = Content.displayName;
const SheetHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />;
SheetHeader.displayName = 'SheetHeader';
const SheetFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />;
SheetFooter.displayName = 'SheetFooter';
const SheetTitle = forwardRef<ElementRef<typeof Title>, ComponentPropsWithoutRef<typeof Title>>(({
  className,
  ...props
}, ref) => <Title ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />);
SheetTitle.displayName = Title.displayName;
const SheetDescription = forwardRef<ElementRef<typeof Description>, ComponentPropsWithoutRef<typeof Description>>(({
  className,
  ...props
}, ref) => <Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
SheetDescription.displayName = Description.displayName;
export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger };